import { useState, useEffect, useCallback } from 'react';

/**
 * Hook personalizado para gestionar la instalación de la PWA.
 * 
 * Captura el evento `beforeinstallprompt` del navegador y expone:
 * - `canInstall`: booleano que indica si la app puede instalarse
 * - `isInstalled`: booleano que indica si ya fue instalada (standalone)
 * - `promptInstall`: función para disparar el diálogo de instalación nativo
 * 
 * El evento solo se dispara si el navegador detecta que:
 * 1. Hay un manifest.json válido
 * 2. Hay un Service Worker activo
 * 3. La app no está ya instalada
 */
const usePWAInstall = () => {
    const [deferredPrompt, setDeferredPrompt] = useState(null);
    const [canInstall, setCanInstall] = useState(false);
    const [isInstalled, setIsInstalled] = useState(false);
    const [isIOS, setIsIOS] = useState(false);
    const [isAndroid, setIsAndroid] = useState(false);

    useEffect(() => {
        // Detectar si es iOS o Android
        const userAgent = navigator.userAgent || navigator.vendor || window.opera;
        const isIOSDevice = /iPad|iPhone|iPod/.test(userAgent) && !window.MSStream;
        const isAndroidDevice = /Android/i.test(userAgent);

        setIsIOS(isIOSDevice);
        setIsAndroid(isAndroidDevice);

        const isRunningStandalone =
            window.matchMedia('(display-mode: standalone)').matches ||
            window.matchMedia('(display-mode: window-controls-overlay)').matches ||
            window.navigator.standalone === true;

        if (isRunningStandalone) {
            setIsInstalled(true);
            return;
        }

        const handleBeforeInstallPrompt = (e) => {
            e.preventDefault();
            setDeferredPrompt(e);
            setCanInstall(true);
        };

        const handleAppInstalled = () => {
            setIsInstalled(true);
            setCanInstall(false);
            setDeferredPrompt(null);
        };

        window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
        window.addEventListener('appinstalled', handleAppInstalled);

        return () => {
            window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
            window.removeEventListener('appinstalled', handleAppInstalled);
        };
    }, []);

    const promptInstall = useCallback(async () => {
        if (!deferredPrompt) return false;

        try {
            deferredPrompt.prompt();
            const { outcome } = await deferredPrompt.userChoice;

            if (outcome === 'accepted') {
                setIsInstalled(true);
                setCanInstall(false);
            }

            setDeferredPrompt(null);
            return outcome === 'accepted';
        } catch (err) {
            console.error('Error al intentar instalar PWA:', err);
            return false;
        }
    }, [deferredPrompt]);

    return {
        canInstall,
        isInstalled,
        isIOS,
        isAndroid,
        promptInstall
    };
};

export default usePWAInstall;
